import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';
import LanguageDropdown from '../components/LanguageDropdown';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  useEffect(() => {
    // Restore language from signup
    const savedLanguage = localStorage.getItem('signupLanguage');
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    const verifyEmail = async () => {
      // Validate params
      if (!token || !email) {
        setStatus('error');
        setMessage(t('messages.invalidVerificationLink') || 'Invalid verification link');
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 
          `${window.location.protocol}//${window.location.host}`;
        
        const response = await axios.post(
          `${backendUrl}/api/auth/verify-email?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`
        );

        setStatus('success');
        setMessage(response.data.message || 'Email verified successfully');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        const errorMsg = error.response?.data?.detail || 
          t('messages.verificationFailed') || 
          'Email verification failed';
        setMessage(errorMsg);
        toast.error(errorMsg);
      }
    };

    verifyEmail();
  }, [token, email, navigate]);

  useEffect(() => {
    // Show success toast when verification is complete
    if (status === 'success') {
      toast.success(t('messages.emailVerified') || 'Email verified! You can now log in.');
    }
  }, [status, t]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-purple-700 text-white flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <img src="/idemenu-logo.svg" alt="idemenu" className="h-10 w-auto" />
          <LanguageDropdown variant="light" />
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-4">
        {/* Decorative blobs */}
        <div className="pointer-events-none absolute -top-16 -left-16 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 right-0 w-72 h-72 bg-white/10 rounded-full blur-3xl" />

        <div className="bg-white text-gray-900 rounded-2xl shadow-2xl p-8 sm:p-12 max-w-md w-full relative">
          <div className="absolute inset-0 rounded-2xl border border-white/60 pointer-events-none" />
        
        <div className="flex flex-col items-center text-center">
          {status === 'loading' && (
            <>
              <Loader className="w-16 h-16 text-purple-600 animate-spin mb-4" />
              <h2 className="text-2xl font-bold mb-2">{t('auth.verifying') || 'Verifying Email'}</h2>
              <p className="text-gray-600">{t('auth.pleaseWait') || 'Please wait while we verify your email address...'}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
              <h2 className="text-2xl font-bold mb-2 text-green-600">
                {t('auth.verified') || 'Email Verified'}
              </h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <p className="text-sm text-gray-500 mb-6">
                {t('auth.redirectingToLogin') || 'Redirecting to login...'}
              </p>
              <Link
                to="/login"
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all"
              >
                {t('auth.goToLogin') || 'Go to Login'}
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
              <h2 className="text-2xl font-bold mb-2 text-red-600">
                {t('auth.verificationFailed') || 'Verification Failed'}
              </h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="space-y-3 w-full">
                <Link
                  to="/login"
                  className="block px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all text-center"
                >
                  {t('auth.backToLogin') || 'Back to Login'}
                </Link>
                <Link
                  to="/signup"
                  className="block px-6 py-3 border border-purple-600 text-purple-600 font-semibold rounded-lg hover:bg-purple-50 transition-all text-center"
                >
                  {t('auth.signup') || 'Create New Account'}
                </Link>
              </div>
            </>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
